import Papa from 'papaparse';
import { ISetting } from '@biolibtech/biolib-js';
import { normalizeColumnName } from './columnUtils';

export interface ITableRecord {
    [columnIndex: number]: string;
}

export interface ICsvParseResult {
    records: ITableRecord[];
    detectedColumns: number;
    detectedColumnNames: string[];
}

export interface ICsvData {
    headers: string[];
    rows: string[][];
}

export interface CsvAutoImportResult {
    shouldAutoImport: boolean;
    columnMapping?: Record<number, number>;
    hasHeaders?: boolean;
}

export function parseTabDelimitedPaste(rawText: string): string[][] {
    const parseResult = Papa.parse<string[]>(rawText, {
        delimiter: '\t',
        header: false,
        skipEmptyLines: false,
    });

    if (parseResult.errors.length > 0) {
        return rawText.split('\n').map((row) => row.replace(/\r$/, '').split('\t'));
    }

    return parseResult.data as string[][];
}

class ValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ValidationError';
    }
}

export function parseCsv(csvText: string, config: any): ICsvParseResult {
    const columns = config?.columns || [];
    const ymlColumnNames = columns.map((col: any) => col.name || '');

    const parseResult = Papa.parse(csvText.trim(), {
        header: false,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
        transform: (value: string) => value.trim(),
    });

    if (parseResult.errors.length > 0) {
        throw new ValidationError(`CSV parsing error: ${parseResult.errors[0].message}`);
    }

    const data = parseResult.data as string[][];

    if (data.length === 0) {
        return {
            records: [],
            detectedColumns: ymlColumnNames.length || 0,
            detectedColumnNames: ymlColumnNames.length > 0 ? ymlColumnNames : [],
        };
    }

    const csvHeaderRow = data[0];
    const csvColumnNames = csvHeaderRow.map((header) => header?.trim() || '');

    const csvToYmlMapping: Record<number, number> = {};
    csvColumnNames.forEach((csvColName, csvIndex) => {
        const ymlIndex = ymlColumnNames.findIndex((ymlColName) => ymlColName === csvColName);
        if (ymlIndex !== -1) {
            csvToYmlMapping[csvIndex] = ymlIndex;
        }
    });

    const records: ITableRecord[] = [];
    const numYmlColumns = ymlColumnNames.length || Math.max(...Object.values(csvToYmlMapping)) + 1 || 2;

    for (let i = 1; i < data.length; i++) {
        const csvRow = data[i];
        const record: ITableRecord = {};

        for (let j = 0; j < numYmlColumns; j++) {
            record[j] = '';
        }

        csvRow.forEach((csvValue, csvIndex) => {
            const ymlIndex = csvToYmlMapping[csvIndex];
            if (ymlIndex !== undefined) {
                record[ymlIndex] = csvValue || '';
            }
        });

        records.push(record);
    }

    return {
        records,
        detectedColumns: numYmlColumns,
        detectedColumnNames: ymlColumnNames.length > 0 ? ymlColumnNames : ['Column 1', 'Column 2'],
    };
}

export function parseCsvToData(csvText: string): ICsvData {
    const parseResult = Papa.parse(csvText.trim(), {
        header: false,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
        transform: (value: string) => value.trim(),
    });

    if (parseResult.errors.length > 0) {
        throw new Error(`CSV parsing error: ${parseResult.errors[0].message}`);
    }

    const data = parseResult.data as string[][];

    if (data.length === 0) {
        return { headers: [], rows: [] };
    }

    return {
        headers: data[0] || [],
        rows: data,
    };
}

export function shouldAutoImportCsv(csvData: ICsvData, expectedColumns: string[]): CsvAutoImportResult {
    if (csvData.headers.length === 0 || csvData.rows.length === 0) {
        return { shouldAutoImport: false };
    }

    const hasHeaders = csvData.headers.some((header) => header.trim() !== '');

    if (!hasHeaders) {
        return { shouldAutoImport: false };
    }

    const mapping: Record<number, number> = {};
    const normalizedHeaders = csvData.headers.map(normalizeColumnName);

    expectedColumns.forEach((expectedCol, expectedIdx) => {
        const normalizedExpectedCol = normalizeColumnName(expectedCol);
        const csvColIdx = normalizedHeaders.indexOf(normalizedExpectedCol);
        if (csvColIdx !== -1) {
            mapping[expectedIdx] = csvColIdx;
        }
    });

    const allColumnsMapped = expectedColumns.every((_, idx) => mapping[idx] !== undefined);

    if (allColumnsMapped) {
        return {
            shouldAutoImport: true,
            columnMapping: mapping,
            hasHeaders: true,
        };
    }

    return { shouldAutoImport: false };
}

export function convertCsvToTableInputRows(
    csvData: ICsvData,
    columnConfigs: any[],
    columnMapping: Record<number, number>,
    hasHeaders: boolean,
): Record<number, Record<number, string>> {
    const rows: Record<number, Record<number, string>> = {};
    const numColumns = columnConfigs?.length || 2;
    const startRow = hasHeaders ? 1 : 0;

    for (let i = startRow; i < csvData.rows.length; i++) {
        const csvRow = csvData.rows[i];
        const outputRowIndex = i - startRow;
        rows[outputRowIndex] = {};

        for (let j = 0; j < numColumns; j++) {
            const csvColIndex = columnMapping?.[j] ?? j;
            const value = csvRow[csvColIndex];
            rows[outputRowIndex][j] = value !== undefined && value !== null ? String(value) : '';
        }
    }

    return rows;
}

export function validateTableInputRows(rows: Record<number, Record<number, string>>, setting: ISetting): null | string {
    const columns = setting.config?.columns || [];
    const rowNumbers = Object.keys(rows)
        .map(Number)
        .sort((a, b) => a - b);
    let isEmpty = true;

    for (const rowNumber of rowNumbers) {
        const row = rows[rowNumber];

        const hasAnyValue = Object.values(row).some((value) => value?.trim());
        if (!hasAnyValue) {
            continue;
        }

        isEmpty = false;

        for (let colIndex = 0; colIndex < columns.length; colIndex++) {
            const column = columns[colIndex] as any;
            const value = row[colIndex]?.trim();

            if (!value) {
                if (column?.required !== false) {
                    return `Row ${rowNumber + 1}, Column "${column?.name || colIndex + 1}": This field is required`;
                }
                continue;
            }

            if (column?.type === 'number') {
                const numValue = parseFloat(value);
                if (isNaN(numValue)) {
                    return `Row ${rowNumber + 1}, Column "${column?.name || colIndex + 1}": Expected a number, got "${value}"`;
                }
            }

            if (column?.type === 'dropdown' && column.options) {
                const allowedValues = Object.values(column.options).map(String);
                if (!allowedValues.includes(value)) {
                    return `Row ${rowNumber + 1}, Column "${column?.name || colIndex + 1}": Invalid value "${value}". Must be one of: ${allowedValues.join(', ')}`;
                }
            }
        }
    }

    if (isEmpty && setting.required && !setting.default_value) {
        return 'Please fill out at least one row.';
    }

    return null;
}
